import { z } from "zod";
/** Participant-facing study contact. Consent, end, and deletion text all use this. */
export const CONTACT_EMAIL = "erik.lang@nhh.no";
export const PhaseKeySchema = z.enum([
    "consent",
    "instructions",
    "base_brief",
    "d1",
    "ai_brief",
    "d2",
    "intervention",
    "d3",
    "survey",
    "end",
]);
export const CorePhaseKeySchema = z.enum([
    "base_brief",
    "d1",
    "ai_brief",
    "d2",
    "intervention",
    "d3",
]);
export const DecisionKeySchema = z.enum(["d1", "d2", "d3"]);
export const PhaseTypeSchema = z.enum(["documents", "decision", "page", "survey"]);
export const ParticipantStatusSchema = z.enum([
    "joined",
    "consented",
    "in_progress",
    "completed",
    "abandoned",
    "withdrawn",
    "excluded",
]);
export const RunStatusSchema = z.enum(["draft", "open", "frozen", "closed", "archived"]);
export const SubmitReasonSchema = z.enum([
    "not_reached",
    "abandoned_draft",
    "timeout",
    "explicit",
]);
/** Precedence: higher number wins. */
export const SUBMIT_REASON_PRECEDENCE = {
    not_reached: 0,
    abandoned_draft: 1,
    timeout: 2,
    explicit: 3,
};
export const DeviceClassSchema = z.enum(["desktop", "tablet", "phone", "unknown"]);
export const ArmRatioSchema = z.object({
    ratio: z.number().int().positive(),
    intervention: z.string().min(1),
});
export const PhaseConfigSchema = z.object({
    key: CorePhaseKeySchema,
    type: z.enum(["documents", "decision"]),
    source: z.string().optional(),
    prompt: z.string().optional(),
    min_ms: z.number().int().nonnegative().optional(),
    max_ms: z.number().int().positive(),
    show_previous: z.boolean().optional(),
    visible: z.array(z.string()).optional(),
});
export const SurveyRoleSchema = z.enum([
    "chair",
    "board_member",
    "executive",
    "consultant",
    "other",
]);
export const SurveyDivisionSchema = z.enum(["tax_legal", "audit", "advisory", "other"]);
/** Which questionnaire a run uses. Missing on older runs means board members. */
export const SurveyAudienceSchema = z.enum(["board", "consulting_partners"]);
export function surveyAudienceOf(config) {
    return config.survey_audience === "consulting_partners" ? "consulting_partners" : "board";
}
export const SurveyGenderSchema = z.enum([
    "male",
    "female",
    "other",
    "prefer_not_to_say",
]);
export const SurveyAiBoardUseSchema = z.enum([
    "no_no_plans",
    "no_interested",
    "yes_experimented",
    "yes_sometimes",
    "yes_often",
    "not_applicable",
]);
/** Grades that mean the person uses AI, including having only tried it. */
export const AI_YES_ANSWERS = [
    "yes_experimented",
    "yes_sometimes",
    "yes_often",
];
export function surveyShowsFollowUps(ai) {
    return ai != null && AI_YES_ANSWERS.includes(ai);
}
/** How many survey questions are in the path (9, or 10 when AI uses are asked). */
export function surveyQuestionCount(ai) {
    return surveyShowsFollowUps(ai) ? 10 : 9;
}
/** True when the multi-select has a real selection, including “None of these”. */
export function surveyAiUsesAnswered(a) {
    return (a.ai_use_none === true ||
        a.ai_board_admin === true ||
        a.ai_analysis === true ||
        a.ai_monitor_outside === true);
}
export const SurveyAnswersSchema = z.object({
    risk_general: z.number().int().min(0).max(10).nullable(),
    risk_financial: z.number().int().min(0).max(10).nullable(),
    role: SurveyRoleSchema.nullable(),
    role_other: z.string().max(200).nullable().default(null),
    years_strategic: z.number().int().min(0).max(80).nullable(),
    age: z.number().int().min(18).max(100).nullable(),
    gender: SurveyGenderSchema.nullable(),
    ai_board_use: SurveyAiBoardUseSchema.nullable(),
    ai_board_admin: z.boolean().nullable(),
    ai_analysis: z.boolean().nullable(),
    ai_monitor_outside: z.boolean().nullable(),
    ai_use_none: z.boolean().nullable().default(null),
    trust_ai_board_pack: z.number().int().min(0).max(10).nullable().default(null),
    trust_ai_brief: z.number().int().min(0).max(10).nullable().default(null),
    division: SurveyDivisionSchema.nullable().default(null),
});
export const emptySurveyAnswers = () => ({
    risk_general: null,
    risk_financial: null,
    role: null,
    role_other: null,
    years_strategic: null,
    age: null,
    gender: null,
    ai_board_use: null,
    ai_board_admin: null,
    ai_analysis: null,
    ai_monitor_outside: null,
    ai_use_none: null,
    trust_ai_board_pack: null,
    trust_ai_brief: null,
    division: null,
});
/** Clear AI-use ticks when the answer is not Yes. “None of these” clears the other ticks. */
export function normalizeSurveyAnswers(a, audience = "board") {
    const trust_ai_board_pack = a.trust_ai_board_pack ?? null;
    const trust_ai_brief = a.trust_ai_brief ?? null;
    const uses = normalizeAiUses(a);
    if (audience === "consulting_partners") {
        return {
            ...a,
            ...uses,
            role: null,
            role_other: null,
            division: a.division ?? null,
            trust_ai_board_pack,
            trust_ai_brief,
        };
    }
    const role_other = a.role === "other" ? (a.role_other == null ? null : a.role_other) : null;
    return {
        ...a,
        ...uses,
        role_other,
        division: null,
        trust_ai_board_pack,
        trust_ai_brief,
    };
}
function normalizeAiUses(a) {
    if (!surveyShowsFollowUps(a.ai_board_use)) {
        return {
            ai_board_admin: null,
            ai_analysis: null,
            ai_monitor_outside: null,
            ai_use_none: null,
        };
    }
    const ai_board_admin = a.ai_board_admin === true;
    const ai_analysis = a.ai_analysis === true;
    const ai_monitor_outside = a.ai_monitor_outside === true;
    if (ai_board_admin || ai_analysis || ai_monitor_outside) {
        return {
            ai_board_admin,
            ai_analysis,
            ai_monitor_outside,
            ai_use_none: false,
        };
    }
    if (a.ai_use_none === true) {
        return {
            ai_board_admin: false,
            ai_analysis: false,
            ai_monitor_outside: false,
            ai_use_none: true,
        };
    }
    return {
        ai_board_admin: null,
        ai_analysis: null,
        ai_monitor_outside: null,
        ai_use_none: null,
    };
}
export function surveyAnswersComplete(a, audience = "board") {
    const n = normalizeSurveyAnswers(a, audience);
    if (n.risk_general == null ||
        n.risk_financial == null ||
        n.years_strategic == null ||
        n.age == null ||
        n.gender == null ||
        n.ai_board_use == null ||
        n.trust_ai_board_pack == null ||
        n.trust_ai_brief == null) {
        return false;
    }
    if (audience === "consulting_partners") {
        if (n.division == null)
            return false;
    }
    else if (n.role == null) {
        return false;
    }
    else if (n.role === "other") {
        const other = (n.role_other ?? "").trim();
        if (!other || other.length > 200)
            return false;
    }
    if (surveyShowsFollowUps(n.ai_board_use)) {
        return surveyAiUsesAnswered(n);
    }
    return true;
}
export const DEFAULT_SURVEY_MAX_MS = 10 * 60_000;
export const RunConfigSchema = z.object({
    arms: z.record(ArmRatioSchema).refine((a) => Object.keys(a).length >= 2, {
        message: "at least two arms required",
    }),
    ai_brief: z.string().min(1),
    phases: z.array(PhaseConfigSchema).min(1),
    slider: z.object({
        start: z.literal("blank"),
        step: z.number().positive().default(1),
    }),
    justification: z.object({
        required: z.boolean(),
        min_chars: z.number().int().nonnegative(),
        max_chars: z.number().int().positive(),
    }),
    window: z.object({
        timezone: z.literal("Europe/Berlin"),
        earliest_start: z.string().min(1),
        latest_start: z.string().min(1),
        link_opens_minutes_before: z.number().int().nonnegative().default(60),
    }),
    abandon_after_ms: z.number().int().positive().default(10 * 60_000),
    cap: z.number().int().positive().default(1000),
    block_size: z.number().int().positive().default(4),
    decision_options: z.object({
        option_a: z.string().min(1),
        option_b: z.string().min(1),
        budget_total: z.number().int().positive(),
        currency: z.string().min(1),
    }),
    survey_enabled: z.boolean().default(true),
    survey_audience: SurveyAudienceSchema.default("board"),
    survey_max_ms: z.number().int().positive().default(DEFAULT_SURVEY_MAX_MS),
});
export const DEFAULT_PHASES = [
    { key: "base_brief", type: "documents", source: "base", min_ms: 10 * 60_000, max_ms: 10 * 60_000 },
    { key: "d1", type: "decision", prompt: "decisions/d1", max_ms: 2 * 60_000 },
    { key: "ai_brief", type: "documents", source: "ai_brief", min_ms: 2 * 60_000, max_ms: 2 * 60_000 },
    {
        key: "d2",
        type: "decision",
        prompt: "decisions/d2",
        max_ms: 2 * 60_000,
        show_previous: true,
    },
    {
        key: "intervention",
        type: "documents",
        source: "arm.intervention",
        min_ms: 2 * 60_000,
        max_ms: 2 * 60_000,
    },
    {
        key: "d3",
        type: "decision",
        prompt: "decisions/d3",
        max_ms: 2 * 60_000,
        show_previous: true,
        visible: ["arm.intervention"],
    },
];
export function defaultRunConfig(overrides) {
    const now = new Date();
    const earliest = new Date(now.getTime() + 2 * 60_000);
    const latest = new Date(earliest.getTime() + 5 * 60_000);
    const fmt = (d) => {
        return d
            .toLocaleString("sv-SE", { timeZone: "Europe/Berlin" })
            .replace(" ", "T")
            .slice(0, 16);
    };
    return RunConfigSchema.parse({
        arms: {
            treatment: { ratio: 1, intervention: "treatment/t1-direct-memo" },
            control: { ratio: 1, intervention: "placebo/c1-market-update" },
        },
        ai_brief: "ai-brief/a1-standard",
        phases: DEFAULT_PHASES,
        slider: { start: "blank", step: 1 },
        justification: { required: false, min_chars: 0, max_chars: 1000 },
        window: {
            timezone: "Europe/Berlin",
            earliest_start: fmt(earliest),
            latest_start: fmt(latest),
            link_opens_minutes_before: 60,
        },
        abandon_after_ms: 10 * 60_000,
        cap: 1000,
        block_size: 4,
        decision_options: {
            option_a: "Sweden",
            option_b: "Denmark",
            budget_total: 100_000_000,
            currency: "NOK",
        },
        survey_enabled: true,
        survey_max_ms: DEFAULT_SURVEY_MAX_MS,
        ...overrides,
    });
}
/** Round half up to nearest integer. */
export function roundHalfUp(n) {
    return Math.floor(n + 0.5);
}
export function amountsFromShare(budget, share) {
    if (share === null || share === undefined) {
        return { amountA: null, amountB: null };
    }
    const amountA = roundHalfUp((budget * share) / 100);
    return { amountA, amountB: budget - amountA };
}
export function canReplaceSubmitReason(existing, incoming) {
    if (!existing)
        return true;
    return SUBMIT_REASON_PRECEDENCE[incoming] > SUBMIT_REASON_PRECEDENCE[existing];
}
export const CORE_PHASE_ORDER = [
    "base_brief",
    "d1",
    "ai_brief",
    "d2",
    "intervention",
    "d3",
];
export function nextCorePhase(key) {
    const i = CORE_PHASE_ORDER.indexOf(key);
    if (i < 0 || i === CORE_PHASE_ORDER.length - 1)
        return "end";
    return CORE_PHASE_ORDER[i + 1];
}
/** Next phase after a core phase, respecting survey_enabled. */
export function nextPhaseAfter(key, config) {
    if (key === "survey")
        return "end";
    const next = nextCorePhase(key);
    if (next === "end") {
        return config.survey_enabled ? "survey" : "end";
    }
    return next;
}
export function isDecisionPhase(key) {
    return key === "d1" || key === "d2" || key === "d3";
}
export function confidenceValue(touched, confidence) {
    if (!touched)
        return null;
    if (confidence == null)
        return null;
    return confidence;
}
//# sourceMappingURL=domain.js.map